import { PlusOutlined } from '@ant-design/icons'
import { Button, Card, Form, FormInstance, message, Space } from 'antd'
import { useWatch } from 'antd/es/form/Form'
import { BoosterMilestoneFormValues, BoosterProgramFormValues } from 'Modules'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  BOOSTER_PROGRAM_MAX_MILESTONES,
  BOOSTER_PROGRAM_MESSAGES,
  DEFAULT_BOOSTER_ENROLLMENT_PERIOD_UNIT,
} from 'constants/booster-program'
import {
  useAddBoosterMilestones,
  useCreateBoosterProgram,
  useDeleteBoosterMilestone,
  useUpdateBoosterProgram,
  useUploadBoosterProgramWhitelist,
} from 'hooks/booster-program'
import { PAGES } from 'routes'

import { hasAllRequired, hasAnyRequired } from './form-state'
import { MilestoneCard } from './milestone-card'
import { ProgramSettings } from './program-settings'
import { isFormValidateError } from './utils'

const DEFAULT_MILESTONE: Partial<BoosterMilestoneFormValues> = {
  rewardType: 'DISCOUNT_CODE',
  codeExpiry: '30_days',
  tokenType: 'MEY',
  ptoSoldOut: false,
  linkedPtos: [],
}

const DEFAULT_PROGRAM_VALUES: Partial<BoosterProgramFormValues> = {
  enrollmentPeriodUnit: DEFAULT_BOOSTER_ENROLLMENT_PERIOD_UNIT,
  targetUsers: 'ALL',
  participantLimitType: 'UNLIMITED',
  startingFillNumber: 0,
  increaseByPercent: 0,
  increaseEvery: 0,
  increaseEveryUnit: 'days',
  milestones: [DEFAULT_MILESTONE as BoosterMilestoneFormValues],
}

type FooterActionsProps = {
  form: FormInstance<BoosterProgramFormValues>
  submitting: boolean
  savingDraft: boolean
  onCancel: () => void
  onCreate: () => void
  onSaveDraft: () => void
}

const FooterActions = ({
  form,
  submitting,
  savingDraft,
  onCancel,
  onCreate,
  onSaveDraft,
}: FooterActionsProps) => {
  const values = (useWatch([], form) ?? {}) as Partial<BoosterProgramFormValues>
  const canCreate = hasAllRequired(values)
  const canSaveDraft = hasAnyRequired(values)

  return (
    <div className="booster-program-create__footer">
      <Space>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          type="primary"
          className="booster-program-create__primary-btn"
          loading={submitting}
          // disabled={!canCreate}
          onClick={onCreate}
        >
          Create
        </Button>
        <Button
          type="primary"
          className="booster-program-create__primary-btn"
          loading={savingDraft}
          disabled={!canSaveDraft}
          onClick={onSaveDraft}
        >
          Save as draft
        </Button>
      </Space>
    </div>
  )
}

interface BoosterProgramCreateFormProps {
  /** Pre-fill values when continuing a draft. Falls back to DEFAULT_PROGRAM_VALUES. */
  initialValues?: Partial<BoosterProgramFormValues>
  /** When set, save calls PATCH on this id instead of POST (draft edit mode). */
  draftId?: string
}

export const BoosterProgramCreateForm = ({
  initialValues,
  draftId,
}: BoosterProgramCreateFormProps = {}) => {
  const [form] = Form.useForm<BoosterProgramFormValues>()
  const navigate = useNavigate()
  const createProgram = useCreateBoosterProgram()
  const updateProgram = useUpdateBoosterProgram()
  const addMilestones = useAddBoosterMilestones()
  const deleteMilestone = useDeleteBoosterMilestone()
  const uploadWhitelist = useUploadBoosterProgramWhitelist()
  const [pendingAction, setPendingAction] = useState<'create' | 'draft' | null>(null)
  const [removingMilestone, setRemovingMilestone] = useState<number | null>(null)
  const isEditingDraft = !!draftId

  // When editing a draft, milestones loaded from the BE carry an `id` — removing
  // one must delete it server-side before dropping the form row. Rows added
  // locally (no id) are only removed from the form.
  const handleRemoveMilestone = async (fieldName: number, remove: (index: number) => void) => {
    const milestoneId = form.getFieldValue(['milestones', fieldName, 'id']) as string | undefined
    if (isEditingDraft && milestoneId) {
      setRemovingMilestone(fieldName)
      try {
        await deleteMilestone.mutateAsync({ programId: draftId!, milestoneId })
      } catch {
        message.error('Failed to delete milestone')
        return
      } finally {
        setRemovingMilestone(null)
      }
    }
    remove(fieldName)
  }

  const submit = async (status: 'SCHEDULED' | 'DRAFT') => {
    const isDraft = status === 'DRAFT'
    try {
      const values = await form.validateFields()
      setPendingAction(isDraft ? 'draft' : 'create')
      const result = isEditingDraft
        ? await updateProgram.mutateAsync({ id: draftId!, values, status })
        : await createProgram.mutateAsync({ values, status })

      const programId = result?._id
      const milestones = (values.milestones ?? []).filter(Boolean)

      // Only post milestones on fresh creates — drafts retain their milestones
      // separately via the detail page (BE add-milestones endpoint requires an id).
      if (programId && milestones.length > 0) {
        await addMilestones.mutateAsync({ programId, milestones })
      }
      // Whitelist (targetUser = "others") is uploaded after the program exists —
      // the endpoint replaces the program's entire current whitelist.
      const whitelistFile = form.getFieldValue('whitelistFile') as File | undefined
      if (programId && values.targetUsers === 'OTHERS' && whitelistFile) {
        await uploadWhitelist.mutateAsync({ programId, file: whitelistFile })
      }

      message.success(isDraft ? BOOSTER_PROGRAM_MESSAGES.S02 : BOOSTER_PROGRAM_MESSAGES.S01)
      navigate(`${PAGES.BOOSTER_PROGRAM}/${programId ?? ''}`)
    } catch (err) {
      if (isFormValidateError(err)) return
      message.error(isDraft ? 'Failed to save draft' : 'Failed to create booster program')
    } finally {
      setPendingAction(null)
    }
  }

  const onCreate = () => submit('SCHEDULED')
  const onSaveDraft = () => submit('DRAFT')

  return (
    <Form
      form={form}
      layout="vertical"
      variant="filled"
      className="booster-program-create"
      initialValues={{ ...DEFAULT_PROGRAM_VALUES, ...initialValues }}
      scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
    >
      <Card className="booster-program-create__card">
        <ProgramSettings />
      </Card>

      <Card className="booster-program-create__card">
        <div className="booster-program-create__section-title">
          Milestones &amp; Promo Code Configuration
        </div>

        <Form.List name="milestones">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <MilestoneCard
                  key={field.key}
                  index={index}
                  name={field.name}
                  onRemove={() => handleRemoveMilestone(field.name, remove)}
                  canRemove={fields.length > 1}
                  removing={removingMilestone === field.name}
                />
              ))}

              {fields.length < BOOSTER_PROGRAM_MAX_MILESTONES && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({ ...DEFAULT_MILESTONE })}
                  className="booster-program-create__add-milestone"
                  block
                >
                  Add Milestone
                </Button>
              )}
            </>
          )}
        </Form.List>
      </Card>

      <FooterActions
        form={form}
        submitting={pendingAction === 'create'}
        savingDraft={pendingAction === 'draft'}
        onCancel={() => navigate(PAGES.BOOSTER_PROGRAM)}
        onCreate={onCreate}
        onSaveDraft={onSaveDraft}
      />
    </Form>
  )
}
